import { useEffect, useState, type FormEvent } from 'react';
import { ApiError } from '../../api/http';
import {
  fetchRoadmapSource,
  forgetRoadmapToken,
  refreshRoadmap,
  updateRoadmapSource,
  type RoadmapSourceView,
} from '../../api/roadmap';
import { ca } from '../../i18n/ca';
import { fmtWhen } from './shared';

const errorOf = (err: unknown): string =>
  err instanceof ApiError
    ? (ca.roadmapSource.errors[err.code] ?? ca.roadmapSource.errors.other)
    : ca.roadmapSource.errors.other;

/**
 * Where the roadmap page's file comes from: a GitHub repository, a branch, a
 * path and a key, with a button to fetch it now and what the last check said.
 *
 * The key is written and never read back, like the bot's token beside it: the
 * field is empty whenever the card opens, an empty field keeps the stored one,
 * and the platform only ever says whether there is one.
 */
export function RoadmapSourceCard() {
  const t = ca.roadmapSource;
  const [view, setView] = useState<RoadmapSourceView | null>(null);
  const [repository, setRepository] = useState('');
  const [branch, setBranch] = useState('');
  const [path, setPath] = useState('');
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [notice, setNotice] = useState<{ text: string; ok: boolean } | null>(null);

  useEffect(() => {
    fetchRoadmapSource()
      .then((v) => {
        setView(v);
        setRepository(v.repository);
        setBranch(v.branch);
        setPath(v.path);
      })
      .catch(() => setView(null));
  }, []);

  if (!view) return null;

  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setNotice(null);
    try {
      // The status comes back; the fields keep what was typed.
      setView(await updateRoadmapSource({ repository, branch, path, ...(token ? { token } : {}) }));
      setToken('');
      setNotice({ text: t.saved, ok: true });
    } catch (err) {
      setNotice({ text: errorOf(err), ok: false });
    } finally {
      setBusy(false);
    }
  }

  async function forget() {
    if (!window.confirm(t.forgetConfirm)) return;
    setBusy(true);
    setNotice(null);
    try {
      setView(await forgetRoadmapToken());
      setToken('');
    } catch (err) {
      setNotice({ text: errorOf(err), ok: false });
    } finally {
      setBusy(false);
    }
  }

  async function refresh() {
    setRefreshing(true);
    setNotice(null);
    try {
      const next = await refreshRoadmap();
      setView(next);
      const result = next.lastResult ?? '';
      setNotice(
        t.results[result]
          ? { text: t.results[result], ok: true }
          : { text: t.errors[result] ?? t.errors.http_error, ok: false },
      );
    } catch (err) {
      setNotice({ text: errorOf(err), ok: false });
    } finally {
      setRefreshing(false);
    }
  }

  const when = (iso: string | null) => (iso ? fmtWhen(iso) : t.never);

  return (
    <form className="card form" onSubmit={save} aria-label={t.card}>
      <h2>
        <i className="bi bi-signpost-split" aria-hidden="true" /> {t.card}
      </h2>
      <p className="muted small">{t.intro}</p>
      <label>
        {t.repository}
        <input
          name="roadmapRepository"
          value={repository}
          placeholder="propietari/nom"
          autoComplete="off"
          onChange={(e) => setRepository(e.target.value)}
        />
        <span className="hint">{t.repositoryHint}</span>
      </label>
      <label>
        {t.branch}
        <input
          name="roadmapBranch"
          value={branch}
          autoComplete="off"
          onChange={(e) => setBranch(e.target.value)}
        />
      </label>
      <label>
        {t.path}
        <input
          name="roadmapPath"
          value={path}
          autoComplete="off"
          onChange={(e) => setPath(e.target.value)}
        />
      </label>
      <label>
        {t.token}
        <input
          name="roadmapToken"
          type="password"
          autoComplete="new-password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
        />
        <span className="hint">{t.tokenHint}</span>
        <span className="hint">
          {view.tokenSet
            ? t.tokenSet(view.tokenSetAt ? fmtWhen(view.tokenSetAt) : null)
            : t.tokenUnset}
        </span>
      </label>
      <div className="row actions">
        <button type="submit" disabled={busy}>
          <i className="bi bi-check-lg" aria-hidden="true" /> {t.save}
        </button>
        {view.tokenSet && (
          <button type="button" className="ghost" disabled={busy} onClick={forget}>
            <i className="bi bi-key" aria-hidden="true" /> {t.forget}
          </button>
        )}
        <button
          type="button"
          className="ghost"
          disabled={refreshing || !view.tokenSet || !view.repository}
          onClick={refresh}
        >
          <i className="bi bi-arrow-repeat" aria-hidden="true" />{' '}
          {refreshing ? t.refreshing : t.refresh}
        </button>
      </div>
      {notice && (
        <p className={`notice ${notice.ok ? 'ok' : 'bad'}`} role="status">
          {notice.text}
        </p>
      )}
      <dl className="facts" aria-label={t.status}>
        <dt>{t.status}</dt>
        <dd>{view.fetched ? t.serving.fetched : t.serving.bundled}</dd>
        <dt>{t.lastCheck}</dt>
        <dd>{when(view.lastCheckAt)}</dd>
        <dt>{t.lastChange}</dt>
        <dd>
          {view.lastCommit
            ? t.commit(
                view.lastCommit.slice(0, 7),
                view.lastCommitAt ? fmtWhen(view.lastCommitAt) : null,
              )
            : when(view.lastChangeAt)}
        </dd>
        {view.lastError && (
          <>
            <dt>{t.lastError}</dt>
            <dd className="error">
              {t.errors[view.lastError] ?? t.errors.http_error}
              {view.lastErrorAt && <span className="muted"> · {fmtWhen(view.lastErrorAt)}</span>}
            </dd>
          </>
        )}
      </dl>
    </form>
  );
}
